import { useRef, useState } from "react";
import { toast } from "sonner";
import { t } from "@/lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_MS = 60000;

export const useVoiceRecorder = (onText) => {
  const [state, setState] = useState("idle");
  const [seconds, setSeconds] = useState(0);
  const rec = useRef(null);
  const timers = useRef([]);
  const onTextRef = useRef(onText);
  onTextRef.current = onText;

  const clearTimers = () => {
    timers.current.forEach((t) => clearInterval(t));
    timers.current = [];
  };

  const transcribe = async (blob) => {
    setState("transcribing");
    const form = new FormData();
    form.append("audio", blob, blob.type.includes("mp4") ? "voz.mp4" : "voz.webm");
    try {
      const res = await fetch(`${API}/transcribe`, { method: "POST", body: form });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      if (data.text) onTextRef.current(data.text);
      else toast.info(t("voice.empty"));
    } catch (e) {
      toast.error(t("voice.err"));
    }
    setState("idle");
  };

  const start = async () => {
    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
      toast.error(t("voice.unsupported"));
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = ["audio/webm", "audio/mp4"].find((m) => MediaRecorder.isTypeSupported(m)) || "";
      const r = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      const chunks = [];
      r.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      r.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        clearTimers();
        transcribe(new Blob(chunks, { type: r.mimeType || "audio/webm" }));
      };
      r.start();
      rec.current = r;
      setSeconds(0);
      setState("recording");
      timers.current.push(setInterval(() => setSeconds((s) => s + 1), 1000));
      timers.current.push(setTimeout(() => r.state === "recording" && r.stop(), MAX_MS));
    } catch {
      toast.error(t("voice.denied"));
    }
  };

  const stop = () => rec.current?.state === "recording" && rec.current.stop();

  return { state, seconds, start, stop };
};
